/**
 * <s-carousel> — a horizontally-scrollable "swim lane" that shows multiple items at once.
 *
 * UI-only: it wires the optional prev/next buttons to scroll the track by roughly one page
 * and toggles their `disabled` state at the start/end of the scroll range. The track is a
 * native scroll-snap container, so scrolling, touch/trackpad swiping, and keyboard focus
 * all work without JS — this component only enhances the arrow controls.
 *
 * Expected DOM (rendered by blocks/carousel.liquid):
 *   <s-carousel>
 *     [<button data-carousel-prev> / <button data-carousel-next>]
 *     <div data-carousel-track> …items… </div>
 *   </s-carousel>
 */
class SCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    this.prevButton = this.querySelector("[data-carousel-prev]");
    this.nextButton = this.querySelector("[data-carousel-next]");

    if (!this.track) return;

    this.onPrev = () => this.scrollByPage(-1);
    this.onNext = () => this.scrollByPage(1);
    this.onScroll = () => this.updateControls();

    this.prevButton?.addEventListener("click", this.onPrev);
    this.nextButton?.addEventListener("click", this.onNext);
    this.track.addEventListener("scroll", this.onScroll, { passive: true });

    this.onPointerDown = (event) => this.startDrag(event);
    this.track.addEventListener("pointerdown", this.onPointerDown);
    // A drag that moved should not also follow the link under the pointer.
    this.onClickCapture = (event) => {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressClick = false;
    };
    this.track.addEventListener("click", this.onClickCapture, true);
    this.onDragStart = (event) => event.preventDefault();
    this.track.addEventListener("dragstart", this.onDragStart);

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);

    this.updateControls();
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener("click", this.onPrev);
    this.nextButton?.removeEventListener("click", this.onNext);
    this.track?.removeEventListener("scroll", this.onScroll);
    this.track?.removeEventListener("pointerdown", this.onPointerDown);
    this.track?.removeEventListener("click", this.onClickCapture, true);
    this.track?.removeEventListener("dragstart", this.onDragStart);
    this.resizeObserver?.disconnect();
  }

  /**
   * Scroll the track by ~80% of its visible width in the given direction.
   * @param {number} direction -1 for previous, 1 for next.
   */
  scrollByPage(direction) {
    const amount = this.track.clientWidth * 0.8 * direction;
    this.track.scrollBy({ left: amount, behavior: "smooth" });
  }

  /**
   * Mouse drag-to-scroll (touch and trackpads already scroll natively).
   * Snap and smooth scrolling are paused while dragging, then the track settles on the nearest item.
   * @param {PointerEvent} event
   */
  startDrag(event) {
    if (event.pointerType !== "mouse" || event.button !== 0) return;
    const startX = event.clientX;
    const startScroll = this.track.scrollLeft;
    let moved = false;

    const onMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      if (!moved && Math.abs(delta) < 5) return;
      if (!moved) {
        moved = true;
        this.track.setAttribute("data-dragging", "");
      }
      this.track.scrollLeft = startScroll - delta;
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      if (!moved) return;
      this.suppressClick = true;
      // Let a click fired by this release be swallowed, then forget the flag.
      setTimeout(() => (this.suppressClick = false), 0);
      const left = this.track.scrollLeft;
      this.track.removeAttribute("data-dragging");
      // Restore the dragged position, then glide to the nearest snap point.
      this.track.scrollLeft = left;
      const item = this.track.firstElementChild;
      if (item) {
        const step = item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || "0");
        this.track.scrollTo({ left: Math.round(left / step) * step, behavior: "smooth" });
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }

  /** Disable prev/next when the track is at its respective end. */
  updateControls() {
    if (!this.prevButton && !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= maxScroll - 1;
    const overflowing = maxScroll > 1;

    if (this.prevButton) this.prevButton.disabled = !overflowing || atStart;
    if (this.nextButton) this.nextButton.disabled = !overflowing || atEnd;
  }
}

if (!customElements.get("s-carousel")) {
  customElements.define("s-carousel", SCarousel);
}
